import React from 'react';
import { ExternalLink, ShoppingCart, MapPin, Play, ChevronRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface GoogleAIRichResultsProps {
    data: {
        inline_images?: any[];
        inline_videos?: any[];
        shopping_results?: any[];
        local_results?: any[];
        knowledge_graph?: any;
        references?: any[];
        sources?: any[];
    };
    isDarkMode: boolean;
}

export const GoogleAIRichResults: React.FC<GoogleAIRichResultsProps> = ({ data, isDarkMode }) => {
    if (!data) return null;

    const {
        inline_images = [],
        inline_videos = [],
        shopping_results = [],
        local_results = [],
        knowledge_graph,
        references = [],
        sources = []
    } = data;

    const getSafeHostname = (url: string) => {
        if (!url || typeof url !== 'string') return '';
        try {
            const match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/im);
            return match ? match[1] : url;
        } catch (e) {
            return url || '';
        }
    };

    const handleImageClick = (index: number) => {
        const images = inline_images.map(img => ({
            url: img.link || img.url || img.thumbnail,
            description: img.title || img.source
        }));

        window.dispatchEvent(new CustomEvent('openFullscreenImageModal', {
            detail: { images, initialIndex: index }
        }));
    };

    const bgColor = isDarkMode ? 'bg-white/5' : 'bg-black/5';
    const borderColor = isDarkMode ? 'border-white/10' : 'border-black/10';
    const textColor = isDarkMode ? 'text-white' : 'text-black';
    const subTextColor = isDarkMode ? 'text-white/60' : 'text-black/60';

    return (
        <div className="space-y-6 mt-4 w-full animate-in fade-in slide-in-from-bottom-2 duration-500">

            {/* Knowledge Graph / Summary */}
            {knowledge_graph && (
                <div className={`p-4 rounded-2xl border ${borderColor} ${bgColor}`}>
                    <div className="flex items-start gap-3">
                        {knowledge_graph.image && (
                            <img src={knowledge_graph.image} alt={knowledge_graph.title} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                        )}
                        <div>
                            <h3 className="font-bold text-lg leading-tight">{knowledge_graph.title}</h3>
                            {knowledge_graph.type && <p className={`text-xs ${subTextColor} mb-2 uppercase tracking-wider`}>{knowledge_graph.type}</p>}
                            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {knowledge_graph.description}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Shopping Results */}
            {shopping_results.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 px-1">
                        <ShoppingCart className="w-4 h-4 text-blue-500" />
                        <span className="font-semibold text-sm uppercase tracking-widest opacity-70">Shopping</span>
                    </div>
                    <div className="flex overflow-x-auto gap-4 pb-4 no-scrollbar px-1">
                        {shopping_results.map((product, idx) => {
                            const link = product.link || product.product_link || product.url;
                            const thumbnail = product.thumbnail || product.image;

                            return (
                                <a
                                    key={idx}
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex-shrink-0 block w-52 p-4 rounded-3xl ${bgColor} } hover:border-blue-500/50 hover:bg-blue-500/5 active:scale-[0.98] transition-all duration-300 group cursor-pointer shadow-sm`}
                                >
                                    <div className="relative aspect-square mb-4 overflow-hidden rounded-2xl bg-white flex items-center justify-center border border-black/5">
                                        <img
                                            src={thumbnail}
                                            alt={product.title}
                                            className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-110"
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).src = 'https://www.google.com/favicon.ico';
                                            }}
                                        />
                                    </div>
                                    <p className="text-sm h-10 transition-colors leading-tight">
                                        {product.title.length > 40
                                            ? product.title.slice(0, 40) + "..."
                                            : product.title}
                                    </p>
                                    <div className="flex items-center justify-between mt-auto">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium">{product.price}</span>
                                            {product.old_price && <span className="text-[11px] line-through opacity-40">{product.old_price}</span>}
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <p className={`text-[10px] ${subTextColor} font-medium truncate max-w-[80px]`}>{product.source || product.merchant}</p>
                                        </div>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Images Grid */}
            {inline_images.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 px-1">
                        <span className="font-bold text-sm uppercase tracking-widest opacity-80">Images</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 px-1">
                        {inline_images.slice(0, 6).map((img, idx) => (
                            <div
                                key={idx}
                                onClick={() => handleImageClick(idx)}
                                className="relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer group bg-black/10 shadow-sm hover:ring-2 hover:ring-blue-500/50 transition-all active:scale-95"
                            >
                                <img
                                    src={img.thumbnail || img.link || img.url || img.image}
                                    alt={img.title || 'Search result image'}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).src = 'https://www.google.com/favicon.ico';
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                                    <div className="w-full">
                                        <p className="text-[10px] text-white font-bold truncate">{img.source || img.title || 'Source'}</p>
                                        <p className="text-[9px] text-white/70 truncate">{img.title || ''}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Local Results */}
            {local_results.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 px-1">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span className="font-semibold text-sm uppercase tracking-widest opacity-70">Local Findings</span>
                    </div>
                    <div className="space-y-2 px-1">
                        {local_results.slice(0, 3).map((place, idx) => (
                            <div key={idx} className={`p-4 rounded-2xl border ${borderColor} ${bgColor} flex justify-between items-center group`}>
                                <div className="flex gap-4 items-center">
                                    {place.thumbnail && (
                                        <img src={place.thumbnail} alt={place.title} className="w-12 h-12 rounded-lg object-cover" />
                                    )}
                                    <div>
                                        <h4 className="font-bold text-sm">{place.title}</h4>
                                        <div className="flex items-center gap-2 text-[10px] mt-0.5 opacity-80">
                                            {place.rating && (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-yellow-500 font-bold">★ {place.rating}</span>
                                                    <span>({place.reviews})</span>
                                                </div>
                                            )}
                                            {place.type && <span>• {place.type}</span>}
                                        </div>
                                        <p className={`text-xs mt-1 ${subTextColor}`}>{place.address}</p>
                                    </div>
                                </div>
                                {place.links?.directions && (
                                    <a
                                        href={place.links.directions}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 rounded-full hover:bg-blue-500/10 text-blue-500 transition-colors"
                                    >
                                        <MapPin className="w-5 h-5" />
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Videos */}
            {inline_videos.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 px-1">
                        <Play className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-sm uppercase tracking-widest opacity-70">Videos</span>
                    </div>
                    <div className="flex overflow-x-auto gap-3 pb-2 no-scrollbar px-1">
                        {inline_videos.map((vid, idx) => (
                            <a
                                key={idx}
                                href={vid.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex-shrink-0 w-64 rounded-2xl overflow-hidden border ${borderColor} ${bgColor} group`}
                            >
                                <div className="relative aspect-video">
                                    <img src={vid.thumbnail} alt={vid.title} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
                                            <Play className="w-5 h-5 text-black fill-current ml-0.5" />
                                        </div>
                                    </div>
                                    {vid.duration && (
                                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded backdrop-blur-md">
                                            {vid.duration}
                                        </div>
                                    )}
                                </div>
                                <div className="p-3">
                                    <p className="text-xs font-medium line-clamp-2 mb-1">{vid.title}</p>
                                    <p className={`text-[10px] ${subTextColor} uppercase tracking-tighter`}>{vid.channel} • {vid.posted}</p>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
};
